import { createStore, applyMiddleware } from "redux";
import { thunk } from "redux-thunk";
import { rootReducer } from "state_management";
import authMiddleware from "utils/middleware/authMiddleware";
import { setStore } from "./storeAccess";

import hash from "object-hash";
import { loggedOutState as currentLoggedOutState } from "state_management/auth";
import { initialTemplateState as currentVisualStateTemplate } from "state_management/visual";

// Helper to set an item in local storage
function setLocalStorage(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore errors, the user might have turned localStorage off
  }
}

// Helper to get an item from local storage
function getLocalStorage(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key)) || null;
  } catch {
    // Ignore errors, the user might have turned localStorage off
    return null;
  }
}

// The initial Redux state
const initialState = {};

// Ensure that the auth and visual state we load from browser storage isn't
// formatted in an invalid way. This is used when we deploy the new application,
// to prevent us from loading something the rest of the application doesn't
// expect!
if (hash(getLocalStorage("loggedOutState")) === hash(currentLoggedOutState)) {
  console.log("Loading auth state from browser storage");
  const auth = getLocalStorage("auth");
  if (auth) {
    // Never restore the team picker on refresh — credentials are lost
    auth.pendingMemberships = null;
    initialState.auth = auth;
  }
} else {
  console.warn("Logged out state changed. Discarding stored auth state");
  setLocalStorage("loggedOutState", currentLoggedOutState);
}

if (hash(getLocalStorage("visualStateTemplate")) === hash(currentVisualStateTemplate)) {
  console.log("Loading visual state from browser storage");
  const visual = getLocalStorage("visual");
  if (visual) {
    initialState.visual = visual;
  }
} else {
  console.warn("Visual state template changed. Discarding stored visual state");
  setLocalStorage("visualStateTemplate", currentVisualStateTemplate);
}

// Create the Redux store
const store = createStore(rootReducer, initialState, applyMiddleware(thunk, authMiddleware));
setStore(store);

// Dev-only: expose the store on window for debugging and for the LIMS demo seed script.
// Gated to non-production so it is never present in a customer's production bundle.
if (import.meta.env.MODE !== "production") {
  window.store = store;
}

// Persist visual and auth state to localStorage on change
let previousVisual = null;
let previousAuth = null;
store.subscribe(() => {
  const state = store.getState();
  if (previousVisual !== state.visual) {
    setLocalStorage("visual", state.visual);
    previousVisual = state.visual;
  }
  if (previousAuth !== state.auth) {
    setLocalStorage("auth", state.auth);
    previousAuth = state.auth;
  }
});

export default store;
export const getState = store.getState;
